import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Socket } from 'ngx-socket-io';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FiscalService } from 'src/app/fiscal/fiscal.service';

import { AuthService } from 'src/app/login/auth.service';

import { UserService } from 'src/app/services/user.service';
import {
  Bank,
  Client,
  ClientCar,
  ExportDoc,
  Organization,
  ProductInDoc,
  User,
  Warehouse
} from 'src/typings';
import { environment } from 'src/environments/environment';
import { normalizeDateRangePayload } from 'src/app/shared/utils/date-filter.util';
import { SaleCellAllocation, SaleSnapshot, SaleStockPlan, SaleStockPlanRequest } from './sale-stock-plan';

export interface DailyMoneySummaryReportMoneyTypeRow {
  money_type: string;
  customer_payments: number;
  customer_returns: number;
  payment_correction_returns: number;
  service_inflow: number;
  service_write_off: number;
  service_fee: number;
  transfer_in: number;
  transfer_out: number;
  transfer_fee: number;
  inflow: number;
  outflow: number;
  net: number;
}

export interface DailyMoneySummaryReportDayRow {
  date: string;
  money_types: DailyMoneySummaryReportMoneyTypeRow[];
  totals: Omit<DailyMoneySummaryReportMoneyTypeRow, 'money_type'> & {
    returns_total: number;
    returns_cash: number;
    returns_terminal: number;
    shipment_amount: number;
  };
}

export interface DailyMoneySummaryReportResponse {
  period: {
    requested_from: string;
    requested_to: string;
  };
  filters: {
    user_id?: number;
    money_type?: string;
  };
  items: DailyMoneySummaryReportDayRow[];
  totals: Omit<DailyMoneySummaryReportMoneyTypeRow, 'money_type'> & {
    returns_total: number;
    returns_cash: number;
    returns_terminal: number;
    shipment_amount: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  user: User | undefined;

  docGw = this.socket.fromEvent<string>('newSell');

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private userService: UserService,
    private fiscalService: FiscalService,
    private socket: Socket
  ) {
    this.userService.user.subscribe((user) => {
      this.user = user;
    });
  }

  getList(): Observable<
    {
      id: number;
      date: Date;
      date_created: Date;
      manager: string;
      client: string;
      warehouse: string;
      paid: boolean;
      bank: string;
      sum: string;
      products: ProductInDoc[];
    }[]
  > {
    return this.http
      .get<
        {
          id: number;
          date: Date;
          date_created: Date;
          manager: string;
          client: string;
          warehouse: string;
          paid: boolean;
          bank: string;
          sum: string;
          products: ProductInDoc[];
        }[]
      >(`${environment.BACKEND}/doc/export/list`)
      .pipe(
        map((data) => {
          return data.map((obj) => {
            return (obj = {
              ...obj,
              date_created: new Date(obj.date_created),
              date: new Date(obj.date)
            });
          });
        })
      );
  }

  searchDocsByProducts(product_ids: number[]) {
    return this.http
      .post<
        {
          id: number;
          date: Date;
          date_created: Date;
          manager: string;
          client: string;
          warehouse: string;
          paid: boolean;
          bank: string;
          sum: string;
          products: ProductInDoc[];
        }[]
      >(`${environment.BACKEND}/doc/export/list`, { product_ids })
      .pipe(
        map((data) => {
          return data.map((obj) => {
            return (obj = {
              ...obj,
              date_created: new Date(obj.date_created),
              date: new Date(obj.date)
            });
          });
        })
      );
  }

  getOne(id: number) {
    return this.http.get<ExportDoc>(`${environment.BACKEND}/doc/export/${id}`);
  }

  stockPlan(payload: SaleStockPlanRequest) {
    return this.http.post<SaleStockPlan>(`${environment.BACKEND}/doc/export/stock-plan`, payload);
  }

  createSnapshot(
    snapshot: SaleSnapshot,
    terminal?: { attemptId: number; requestId: string; profileId?: number },
    allowDebtLimitExceeded = false
  ) {
    const { display_products, version, ...payload } = snapshot;
    return this.http.post<ExportDoc>(`${environment.BACKEND}/doc/export/create?response=compact`, {
      ...payload,
      allowDebtLimitExceeded,
      terminal_attempt_id: terminal?.attemptId,
      terminal_request_id: terminal?.requestId,
      terminal_profile_id: terminal?.profileId
    });
  }

  create(payload: {
    request_id?: string;
    client?: Client;
    clientCar?: ClientCar;
    org?: Organization;
    warehouse?: Warehouse;
    passed?: boolean;
    paid?: boolean;
    fiscal?: boolean;
    bank: Bank;
    date?: Date;
    org_bank_account_id?: number;
    order_id?: number;
    terminal_attempt_id?: number;
    terminal_request_id?: string;
    terminal_profile_id?: number;
    payment_warehouse_id?: number;
    allowDebtLimitExceeded?: boolean;
    products: Array<ProductInDoc & { cell_allocations?: SaleCellAllocation[] }>;
  }) {
    return this.http.post<ExportDoc>(`${environment.BACKEND}/doc/export/create?response=compact`, {
      ...payload,
      warehouse_id: payload.warehouse?.id,
      payment_warehouse_id:
        payload.payment_warehouse_id || this.user?.defaultWarehouse?.id || undefined,
      client_id: payload.client?.id,
      client_car_id: payload.clientCar?.id,
      org_id: payload.org?.id,
      org_bank_account_id: payload.org_bank_account_id,
      bank_id: payload.bank.id,
      products: payload.products?.map((prod) => ({
        product_id: prod.product.id,
        quan: prod.quan,
        price: prod.price,
        cell_allocations: prod.cell_allocations
      }))
    });
  }

  delete(id: number) {
    return this.http.delete(`${environment.BACKEND}/doc/export/${id}`);
  }

  deleteCascade(id: number) {
    return this.http.delete(`${environment.BACKEND}/doc/export/cascade/${id}`);
  }

  getProductsInDoc(id: number): Observable<
    {
      id: number;
      description: string;
      brand: string;
      article: string;
      quan: string;
      price: string;
      sum: string;
    }[]
  > {
    return this.http.get<
      {
        id: number;
        description: string;
        brand: string;
        article: string;
        quan: string;
        price: string;
        sum: string;
      }[]
    >(`${environment.BACKEND}/doc/export/${id}/products`);
  }

  save(payload: {
    id: number;
    client?: Client;
    clientCar?: ClientCar;
    warehouse?: Warehouse;
    passed?: boolean;
    paid?: boolean;
    fiscal?: boolean;
    bank_id?: number;
    date?: Date;
    order_id?: number;
    allowDebtLimitExceeded?: boolean;
    products?: Array<ProductInDoc & { cell_allocations?: SaleCellAllocation[] }>;
  }) {
    return this.http.put<ExportDoc>(`${environment.BACKEND}/doc/export/save`, {
      ...payload,
      warehouse_id: payload.warehouse?.id,
      client_id: payload.client?.id,
      client_car_id: payload.clientCar?.id,
      products: payload.products?.map((prod) => ({
        product_id: prod.product.id,
        quan: prod.quan,
        price: prod.price,
        cell_allocations: prod.cell_allocations
      }))
    });
  }

  search(payload: {
    date_from?: Date;
    date_to?: Date;
    client_id?: number;
    user_id?: number;
    id?: number;
    fiscal?: boolean;
    warehouse_id?: number;
    moneyType?: string;
    paid?: boolean;
  }) {
    const normalizedPayload = normalizeDateRangePayload(payload);
    return this.http
      .post<
        {
          id: number;
          date: Date;
          date_created: Date;
          manager: string;
          client: string;
          client_phone?: string;
          client_car_description?: string;
          client_car_vin?: string;
          warehouse: string;
          paid: boolean;
          fiscal: boolean;
          bank: string;
          sum: string;
          products: ProductInDoc[];
        }[]
      >(`${environment.BACKEND}/doc/export/search`, normalizedPayload)
      .pipe(
        map((data) => {
          return data.map((obj) => {
            return (obj = {
              ...obj,
              date_created: new Date(obj.date_created),
              date: new Date(obj.date)
            });
          });
        })
      );
  }

  getDailyMoneySummaryReport(payload: {
    date_from: Date;
    date_to: Date;
    user_id?: number;
    money_type?: string;
  }) {
    const normalizedPayload = normalizeDateRangePayload(payload);
    return this.http.post<DailyMoneySummaryReportResponse>(
      `${environment.BACKEND}/doc/export/report/daily-money-summary`,
      normalizedPayload
    );
  }
}
